import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useCookies } from "react-cookie";

import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import Container from "react-bootstrap/Container";
import Form from "react-bootstrap/Form";

import NavBar from "../../../components/NavBar";
import styles from "../../../styles/Profile.module.css";
import redcap_styles from "../../../apps/redcap-perms/styles/Main.module.css";

import { tokenIsValid } from "../../../utils/token";
import { NavigateRoutes } from "../../../constants/routes";
import { submitInterview } from "../../../api/Interviews";

const RA_LANGUAGES = ["English", "Spanish"];

const NewInterview = () => {
  const navigate = useNavigate();
  const [cookies] = useCookies(["token"]);
  const [authorized, setAuthorized] = useState(false);
  const [pageDoneLoading, setPageDoneLoading] = useState(false);

  useEffect(() => {
    if (!cookies["token"] || !tokenIsValid(cookies["token"])) {
      navigate(NavigateRoutes.LOGIN);
    } else {
      setAuthorized(true);
    }
    setPageDoneLoading(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [candidateFirstName, setCandidateFirstName] = useState("");
  const [candidateLastName, setCandidateLastName] = useState("");
  const [candidateEmail, setCandidateEmail] = useState("");
  const [candidatePhone, setCandidatePhone] = useState("");
  const [raLanguage, setRaLanguage] = useState(RA_LANGUAGES[0]);
  const [interviewDate, setInterviewDate] = useState("");
  const [interviewTime, setInterviewTime] = useState("");
  const [schedulerEmail, setSchedulerEmail] = useState("");
  const [interviewerFullName, setInterviewerFullName] = useState("");
  const [interviewerEmail, setInterviewerEmail] = useState("");
  const [interviewerPhone, setInterviewerPhone] = useState("");
  const [interviewerZoomId, setInterviewerZoomId] = useState("");
  const [interviewerZoomUrl, setInterviewerZoomUrl] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const resetForm = () => {
    setCandidateFirstName("");
    setCandidateLastName("");
    setCandidateEmail("");
    setCandidatePhone("");
    setRaLanguage(RA_LANGUAGES[0]);
    setInterviewDate("");
    setInterviewTime("");
    setSchedulerEmail("");
    setInterviewerFullName("");
    setInterviewerEmail("");
    setInterviewerPhone("");
    setInterviewerZoomId("");
    setInterviewerZoomUrl("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitSuccess(false);
    setSubmitError("");

    submitInterview({
      candidate_first_name: candidateFirstName,
      candidate_last_name: candidateLastName,
      candidate_email: candidateEmail,
      candidate_phone: candidatePhone,
      ra_language: raLanguage,
      interview_date: interviewDate,
      interview_time: interviewTime,
      scheduler_email: schedulerEmail,
      interviewer_full_name: interviewerFullName,
      interviewer_email: interviewerEmail,
      interviewer_phone: interviewerPhone,
      interviewer_zoom_id: interviewerZoomId,
      interviewer_zoom_url: interviewerZoomUrl,
    })
      .then(() => {
        setSubmitSuccess(true);
        resetForm();
      })
      .catch((err) => {
        const detail =
          err.response?.data?.detail || "An error occurred. Please try again.";
        setSubmitError(detail);
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <div>
      <NavBar />
      {pageDoneLoading && authorized && (
        <Container className={styles.pageContainer}>
          <Container>
            <h1>Schedule RA Interview</h1>
          </Container>

          <Form onSubmit={handleSubmit}>

            <Container className={redcap_styles.container}>
              <Container className={redcap_styles.functionContainer}>
                <h4>Candidate Info</h4>

                <Container className={styles.attributeContainer}>
                  <Container className={styles.attributeName}>First name</Container>
                  <Container>
                    <Form.Control
                      type="text"
                      value={candidateFirstName}
                      onChange={(e) => setCandidateFirstName(e.target.value)}
                      required
                    />
                  </Container>
                </Container>

                <Container className={styles.attributeContainer}>
                  <Container className={styles.attributeName}>Last name</Container>
                  <Container>
                    <Form.Control
                      type="text"
                      value={candidateLastName}
                      onChange={(e) => setCandidateLastName(e.target.value)}
                      required
                    />
                  </Container>
                </Container>

                <Container className={styles.attributeContainer}>
                  <Container className={styles.attributeName}>Email</Container>
                  <Container>
                    <Form.Control
                      type="email"
                      value={candidateEmail}
                      onChange={(e) => setCandidateEmail(e.target.value)}
                      required
                    />
                  </Container>
                </Container>

                <Container className={styles.attributeContainer}>
                  <Container className={styles.attributeName}>Phone</Container>
                  <Container>
                    <Form.Control
                      type="text"
                      value={candidatePhone}
                      onChange={(e) => setCandidatePhone(e.target.value)}
                    />
                  </Container>
                </Container>
              </Container>
            </Container>

            <Container className={redcap_styles.container}>
              <Container className={redcap_styles.functionContainer}>
                <h4>Interview Info</h4>

                <Container className={styles.attributeContainer}>
                  <Container className={styles.attributeName}>RA language</Container>
                  <Container>
                    <Form.Select
                      value={raLanguage}
                      onChange={(e) => setRaLanguage(e.target.value)}
                    >
                      {RA_LANGUAGES.map((lang) => (
                        <option key={lang}>{lang}</option>
                      ))}
                    </Form.Select>
                  </Container>
                </Container>

                <Container className={styles.attributeContainer}>
                  <Container className={styles.attributeName}>Interview date</Container>
                  <Container>
                    <Form.Control
                      type="date"
                      value={interviewDate}
                      onChange={(e) => setInterviewDate(e.target.value)}
                      required
                    />
                  </Container>
                </Container>

                <Container className={styles.attributeContainer}>
                  <Container className={styles.attributeName}>Interview time</Container>
                  <Container>
                    <Form.Control
                      type="time"
                      value={interviewTime}
                      onChange={(e) => setInterviewTime(e.target.value)}
                      required
                    />
                  </Container>
                </Container>

                <Container className={styles.attributeContainer}>
                  <Container className={styles.attributeName}>Scheduler email</Container>
                  <Container>
                    <Form.Control
                      type="email"
                      value={schedulerEmail}
                      onChange={(e) => setSchedulerEmail(e.target.value)}
                      required
                    />
                    <Form.Text muted>
                      Copied on the emails to the interviewer: the confirmation
                      now, and any rescheduling, cancellation or no show later.
                    </Form.Text>
                  </Container>
                </Container>
              </Container>
            </Container>

            <Container className={redcap_styles.container}>
              <Container className={redcap_styles.functionContainer}>
                <h4>Interviewer Info</h4>

                <Container className={styles.attributeContainer}>
                  <Container className={styles.attributeName}>Full name</Container>
                  <Container>
                    <Form.Control
                      type="text"
                      value={interviewerFullName}
                      onChange={(e) => setInterviewerFullName(e.target.value)}
                      required
                    />
                  </Container>
                </Container>

                <Container className={styles.attributeContainer}>
                  <Container className={styles.attributeName}>Email</Container>
                  <Container>
                    <Form.Control
                      type="email"
                      value={interviewerEmail}
                      onChange={(e) => setInterviewerEmail(e.target.value)}
                      required
                    />
                  </Container>
                </Container>

                <Container className={styles.attributeContainer}>
                  <Container className={styles.attributeName}>Phone</Container>
                  <Container>
                    <Form.Control
                      type="text"
                      value={interviewerPhone}
                      onChange={(e) => setInterviewerPhone(e.target.value)}
                    />
                  </Container>
                </Container>

                <Container className={styles.attributeContainer}>
                  <Container className={styles.attributeName}>Zoom meeting ID</Container>
                  <Container>
                    <Form.Control
                      type="text"
                      value={interviewerZoomId}
                      onChange={(e) => setInterviewerZoomId(e.target.value)}
                      required
                    />
                  </Container>
                </Container>

                <Container className={styles.attributeContainer}>
                  <Container className={styles.attributeName}>Zoom meeting URL</Container>
                  <Container>
                    <Form.Control
                      type="text"
                      value={interviewerZoomUrl}
                      onChange={(e) => setInterviewerZoomUrl(e.target.value)}
                      required
                    />
                  </Container>
                </Container>
              </Container>
            </Container>

            {submitSuccess && (
              <Alert variant="success" className="mx-3">
                Interview scheduled. Confirmation emails have been sent.
              </Alert>
            )}

            {submitError && (
              <Alert variant="danger" className="mx-3">
                {submitError}
              </Alert>
            )}

            <Container className={styles.sectionContainer}>
              <Container fluid className={styles.buttonContainer}>
                <Button type="submit" variant="warning" disabled={submitting}>
                  {submitting ? "Submitting..." : "Schedule interview"}
                </Button>
              </Container>
            </Container>

          </Form>
        </Container>
      )}
    </div>
  );
};

export default NewInterview;
